import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { reportApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Swords, Loader2, Copy, Trophy, Plus, LogIn, Crown, Clock, CheckCircle2 } from "lucide-react";

const STATUS_STYLE = {
  open: "text-amber-600 bg-amber-500/10 border-amber-500/30",
  active: "text-cyan-600 bg-cyan-500/10 border-cyan-500/30",
  completed: "text-emerald-600 bg-emerald-500/10 border-emerald-500/30",
};

export default function Battles() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [battles, setBattles] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [selPersona, setSelPersona] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/battles").then((r) => setBattles(r.data)).catch(reportApiError("Battles"));
  useEffect(() => { load(); api.get("/personas").then((r) => setPersonas(r.data)).catch(reportApiError("Battles")); }, []);

  const create = async () => {
    if (!selPersona) return toast.error("Pick a customer first");
    setBusy(true);
    try {
      const { data } = await api.post("/battles", { persona_id: selPersona });
      toast.success(`Battle created! Share code ${data.code}`);
      setCreateOpen(false); setSelPersona("");
      load();
    } catch { toast.error("Couldn't create battle"); } finally { setBusy(false); }
  };

  const join = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      await api.post("/battles/join", { code: joinCode.trim() });
      toast.success("Joined the battle!");
      setJoinCode(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't join"); } finally { setBusy(false); }
  };

  const myRole = (b) => (b.creator.user_id === user.id ? "creator" : "opponent");
  const me = (b) => b[myRole(b)];
  const play = (b) => navigate(`/app/roleplay?battle=${b.id}&persona=${b.persona_id}`);

  if (!battles) return <div className="flex items-center justify-center py-32 text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-red-600 font-mono text-xs uppercase tracking-widest flex items-center gap-2"><Swords className="w-4 h-4" /> Head-to-head</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Sales Battle Mode</h1>
          <p className="text-slate-500 mt-2 max-w-xl">Challenge a friend on the same customer. Higher scorecard wins.</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="create-battle-btn"><Plus className="w-4 h-4 mr-1" /> New Battle</Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200 text-slate-900">
            <DialogHeader><DialogTitle>Create a battle</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-500">Pick the customer you'll both face:</p>
            <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
              {personas.map((p) => (
                <button key={p.id} onClick={() => setSelPersona(p.id)}
                  className={`text-left p-3 rounded-xl border transition-colors ${selPersona === p.id ? "border-emerald-500/60 bg-emerald-500/10" : "border-slate-200 hover:border-white/20"}`}
                  data-testid={`battle-persona-${p.id}`}>
                  <div className="text-sm font-medium text-slate-900">{p.title}</div>
                  <div className="text-[11px] text-slate-500">{p.difficulty}</div>
                </button>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold w-full" data-testid="confirm-create-battle-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & get invite code"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="card-surface rounded-2xl p-5">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2"><LogIn className="w-4 h-4 text-cyan-600" /> Join with a code</h3>
        <div className="flex gap-2 mt-3">
          <Input value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="Enter invite code (e.g. 6DC907)"
            className="bg-slate-50 border-slate-200 text-slate-900 uppercase" data-testid="join-code-input" />
          <Button onClick={join} disabled={busy} className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold" data-testid="join-battle-btn">Join</Button>
        </div>
      </div>

      <div className="space-y-3">
        {battles.length === 0 && <div className="card-surface rounded-2xl p-8 text-center text-slate-500">No battles yet. Create one and challenge a friend.</div>}
        {battles.map((b) => {
          const mine = me(b);
          const role = myRole(b);
          const other = role === "creator" ? b.opponent : b.creator;
          const iWon = b.status === "completed" && (b.winner === role || b.winner === "tie");
          return (
            <motion.div key={b.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-2xl p-5" data-testid={`battle-${b.id}`}>
              <div className="flex items-center gap-4">
                <img src={b.persona_avatar} alt="" className="w-12 h-12 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{b.persona_title}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono uppercase border ${STATUS_STYLE[b.status]}`}>{b.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <Copy className="w-3 h-3" /> Code: <span className="font-mono text-slate-600">{b.code}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-4">
                <div className={`p-3 rounded-xl border ${role === "creator" ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-200"}`}>
                  <div className="text-xs text-slate-500">{b.creator.name}{role === "creator" && " (You)"}</div>
                  <div className="text-2xl font-bold text-slate-900 font-display">{b.creator.rating ?? "—"}</div>
                </div>
                <div className={`p-3 rounded-xl border ${role === "opponent" ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-200"}`}>
                  <div className="text-xs text-slate-500">{b.opponent?.name || "Waiting for opponent…"}{role === "opponent" && " (You)"}</div>
                  <div className="text-2xl font-bold text-slate-900 font-display">{b.opponent?.rating ?? "—"}</div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                {b.status === "completed" ? (
                  <span className={`text-sm font-semibold flex items-center gap-2 ${iWon ? "text-emerald-600" : "text-red-600"}`}>
                    {b.winner === "tie" ? <><Trophy className="w-4 h-4" /> It's a tie!</> : iWon ? <><Crown className="w-4 h-4" /> You won!</> : <>You lost</>}
                  </span>
                ) : mine.played ? (
                  <span className="text-sm text-slate-500 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> You've played. Waiting for {other?.name || "opponent"}.</span>
                ) : b.status === "open" && role === "creator" ? (
                  <span className="text-sm text-slate-500 flex items-center gap-2"><Clock className="w-4 h-4" /> Share the code, then play your round.</span>
                ) : null}
                {!mine.played && (b.status === "active" || (b.status === "open" && role === "creator")) && (
                  <Button onClick={() => play(b)} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid={`play-battle-${b.id}`}>
                    Play my round
                  </Button>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
